import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { NursingHome } from "@/hooks/use-nursing-home";

interface SettingsPageProps {
  nursingHome: NursingHome;
}

export default function SettingsPage() {
  const { nursingHome } = useOutletContext<SettingsPageProps>();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [leadNotifications, setLeadNotifications] = useState(true);
  const [reviewNotifications, setReviewNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  
  // Contact settings
  const [contactEmail, setContactEmail] = useState(nursingHome?.email || "");
  const [contactPhone, setContactPhone] = useState(nursingHome?.phone_number || "");
  
  // Visibility settings
  const [profileVisible, setProfileVisible] = useState(true);
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(true);

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      // In a real implementation, you would save these to the database
      // For now, we'll just show a success message
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success("Notification preferences updated successfully");
    } catch (error) {
      toast.error("Failed to update notification preferences");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('nursing_homes')
        .update({
          email: contactEmail,
          phone_number: contactPhone,
        })
        .eq('nh_slug', nursingHome.nh_slug);

      if (error) throw error;
      
      toast.success("Contact information updated successfully");
    } catch (error) {
      toast.error("Failed to update contact information");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVisibility = async () => {
    setLoading(true);
    try {
      // In a real implementation, you would save visibility settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success("Visibility settings updated successfully");
    } catch (error) {
      toast.error("Failed to update visibility settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your nursing home profile settings and preferences
        </p>
      </div>

      {/* Account Status */}
      <Card>
        <CardHeader>
          <CardTitle>Account Status</CardTitle>
          <CardDescription>
            Your current account status and subscription information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Profile Status</Label>
              <p className="text-sm text-muted-foreground">Your nursing home profile is verified</p>
            </div>
            <Badge variant="default" className="bg-green-100 text-green-800">
              Verified
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Subscription Plan</Label>
              <p className="text-sm text-muted-foreground">Basic plan with standard features</p>
            </div>
            <Badge variant="outline">Basic</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>
            Update your nursing home's contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact-email">Email Address</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contact@nursinghome.ie"
              />
            </div>
            <div>
              <Label htmlFor="contact-phone">Phone Number</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+353 1 234 5678"
              />
            </div>
          </div>
          <Button onClick={handleSaveContact} disabled={loading}>
            {loading ? "Saving..." : "Save Contact Information"}
          </Button>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>
            Choose what notifications you'd like to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive general email notifications</p>
            </div>
            <Switch
              checked={emailNotifications}
              onCheckedChange={setEmailNotifications}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>New Lead Alerts</Label>
              <p className="text-sm text-muted-foreground">Get notified when you receive new leads</p>
            </div>
            <Switch
              checked={leadNotifications}
              onCheckedChange={setLeadNotifications}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Review Notifications</Label>
              <p className="text-sm text-muted-foreground">Get notified about new reviews</p>
            </div>
            <Switch
              checked={reviewNotifications}
              onCheckedChange={setReviewNotifications}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Marketing Emails</Label>
              <p className="text-sm text-muted-foreground">Receive updates and promotional content</p>
            </div>
            <Switch
              checked={marketingEmails}
              onCheckedChange={setMarketingEmails}
            />
          </div>
          <Button onClick={handleSaveNotifications} disabled={loading}>
            {loading ? "Saving..." : "Save Notification Preferences"}
          </Button>
        </CardContent>
      </Card>

      {/* Privacy & Visibility */}
      <Card>
        <CardHeader>
          <CardTitle>Privacy & Visibility</CardTitle>
          <CardDescription>
            Control what information is visible on your public profile
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label>Profile Visibility</Label>
              <p className="text-sm text-muted-foreground">Make your nursing home profile visible to the public</p>
            </div>
            <Switch
              checked={profileVisible}
              onCheckedChange={setProfileVisible}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Show Email Address</Label>
              <p className="text-sm text-muted-foreground">Display email address on your public profile</p>
            </div>
            <Switch
              checked={showEmail}
              onCheckedChange={setShowEmail}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Show Phone Number</Label>
              <p className="text-sm text-muted-foreground">Display phone number on your public profile</p>
            </div>
            <Switch
              checked={showPhone}
              onCheckedChange={setShowPhone}
            />
          </div>
          <Button onClick={handleSaveVisibility} disabled={loading}>
            {loading ? "Saving..." : "Save Visibility Settings"}
          </Button>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card>
        <CardHeader>
          <CardTitle>Data Management</CardTitle>
          <CardDescription>
            Manage your data and account preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Export Data</Label>
              <p className="text-sm text-muted-foreground">Download a copy of your nursing home data</p>
            </div>
            <Button variant="outline">
              Export Data
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label>Account Deactivation</Label>
              <p className="text-sm text-muted-foreground">Temporarily disable your account</p>
            </div>
            <Button variant="outline">
              Deactivate Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}